import { useState, useEffect, useRef } from "react";
import "../style/MultiStackScroll.css";
import Fram1 from "../assets/stack/bank.jpeg";
import Fram4 from "../assets/stack/stripe.jpeg";
import Fram5 from "../assets/stack/cash.jpeg";

const pages = [
  {
    id: 1,
    title: "Bank Transfer",
    subtitle: "Bank details included automatically",
    bullets: [
      "Customers can mark when they've sent payment",
      "You receive an instant notification",
      "You confirm once cleared in your bank"
    ],
    image: Fram1,
  },
  {
    id: 4,
    title: "Card Payments (Stripe)",
    subtitle: "Customers can pay securely online by card",
    bullets: [
      "Payments are processed through Stripe",
      "Status updates are automatic",
      "Funds are transferred directly to your bank account",
    ],
    image: Fram4,
  },
  {
    id: 5,
    title: "Cash Payments",
    subtitle: "Select cash as the payment method when creating the job",
    bullets: [
      "Mark the job as paid once cash has been received",
      "Payment status updates instantly across the platform",
      "Keeps your cashflow records accurate",
    ],
    image: Fram5,
  },
];

const FeatureCard = ({ page }) => {
  return (
    <div className="stack-card">
      {/* Image */}
      <div className="stack-image-wrapper">
        <img src={page.image} alt={page.title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
          className="stack-image" />
      </div>

      {/* Text Content Below Image */}
      <div className="stack-text-content">
        <h3 className="stack-title">{page.title}</h3>
        <p className="stack-subtitle">{page.subtitle}</p>
        <ul className="bullet-list">
          {page.bullets.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const PaymentSection = () => {
  const scrollContainerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const scrollLeft = container.scrollLeft;
      const cardWidth = container.querySelector('.stack-card')?.offsetWidth || 0;
      const gap = isMobile ? 0 : 50;
      
      // Calculate which card is most visible
      const index = Math.round(scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(Math.max(0, index), pages.length - 1));
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial call
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isMobile]);

  const scrollToCard = (index) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = container.querySelector('.stack-card')?.offsetWidth || 0;
    const gap = isMobile ? 0 : 50;
    const scrollPosition = index * (cardWidth + gap);

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      scrollToCard(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < pages.length - 1) {
      scrollToCard(activeIndex + 1);
    }
  };

  return (
    <div className="stack-container" id="features">
      {/* Section Header */}
      <div className="stack-main-header">
        <h2 className="stack-main-title">Payment Methods</h2>
        <p className="stack-main-subtitle">
          Flexible payment options on every quote, giving you complete control.
        </p>
      </div>

      {/* Horizontal Scrollable Cards */}
      <div className="scroll-wrapper">
        {/* Previous Button - Desktop Only */}
        {!isMobile && (
          <button
            className={`nav-button nav-button-left ${activeIndex === 0 ? 'disabled' : ''}`}
            onClick={handlePrevious}
            disabled={activeIndex === 0}
            aria-label="Previous card"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        <div
          className="stack-scroll-container"
          ref={scrollContainerRef}
        >
          {pages.map((page) => (
            <FeatureCard key={page.id} page={page} />
          ))}
        </div>

        {/* Next Button - Desktop Only */}
        {!isMobile && (
          <button
            className={`nav-button nav-button-right ${activeIndex === pages.length - 1 ? 'disabled' : ''}`}
            onClick={handleNext}
            disabled={activeIndex === pages.length - 1}
            aria-label="Next card"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Scroll Indicators (Bubbles) */}
        <div className="scroll-indicators">
          {pages.map((_, index) => (
            <button
              key={index}
              className={`scroll-bubble ${activeIndex === index ? 'active' : ''}`}
              onClick={() => scrollToCard(index)}
              aria-label={`Scroll to card ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentSection;